import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Megaphone, UserCheck, FileSignature, Users, Check, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FaqSection from "@/components/FaqSection";
import WhatsAppFloatingButton, { WHATSAPP_NUMBER } from "@/components/WhatsAppFloatingButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { usePageMeta } from "@/hooks/usePageMeta";
import { submitCrmLead } from "@/lib/leads";
import { THANK_YOU } from "@/lib/thankYou";
import { toast } from "@/hooks/use-toast";
import heroImage from "@/assets/hero-anuncie.jpg";

const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Olá, Paulo! Quero anunciar meu imóvel com a Top Class Imóveis."
)}`;

const reasons = [
  {
    icon: Building2,
    title: "Especialização em alto padrão",
    text: "Ipanema, Leblon, Copacabana, Botafogo, Lagoa, Flamengo, Barra da Tijuca e Recreio.",
  },
  {
    icon: Megaphone,
    title: "Marketing digital completo",
    text: "Campanhas segmentadas, presença nos principais portais e nas redes sociais.",
  },
  {
    icon: UserCheck,
    title: "Atendimento direto com o corretor",
    text: "Paulo Miranda, CRECI-RJ 83590 — sem intermediários genéricos.",
  },
  {
    icon: FileSignature,
    title: "Suporte até a escritura",
    text: "Divulgação, atendimento aos interessados, negociação e apoio em toda a documentação.",
  },
  {
    icon: Users,
    title: "Rede de parceiros",
    text: "Corretores e opcionistas que ampliam o alcance de compradores qualificados.",
  },
];

const steps = [
  { title: "Cadastro", text: "Você preenche o formulário com os dados do imóvel." },
  {
    title: "Vistoria e avaliação técnica",
    text: "Visita para levantar características, condição e valor de mercado sugerido.",
  },
  {
    title: "Produção de conteúdo",
    text: "Fotos profissionais (e vídeo, quando aplicável) que valorizam o imóvel.",
  },
  {
    title: "Divulgação estratégica",
    text: "Anúncio no site, portais imobiliários e campanhas de tráfego pago segmentadas por perfil de comprador.",
  },
  {
    title: "Visitas e negociação",
    text: "Todas as visitas são acompanhadas, com filtragem de interessados e apoio completo até o fechamento e a escritura.",
  },
];

const included = [
  "Avaliação técnica do imóvel sem custo",
  "Sessão de fotos profissionais",
  "Anúncio no site topclassimoveis.com e principais portais",
  "Campanhas de divulgação em redes sociais e Google/Meta Ads",
  "Atendimento e qualificação dos interessados",
  "Acompanhamento de visitas",
  "Suporte em documentação e contrato até a escritura",
];

const faq = [
  {
    question: "Anunciar tem algum custo para o proprietário?",
    answer:
      "Não. O cadastro e a avaliação inicial são gratuitos. A comissão é paga apenas no fechamento do negócio, conforme praxe de mercado.",
  },
  {
    question: "Meu imóvel precisa estar mobiliado ou reformado para anunciar?",
    answer:
      "Não é obrigatório. Durante a vistoria, Paulo Miranda orienta sobre pequenos ajustes que podem valorizar o imóvel nas fotos e visitas.",
  },
  {
    question: "Em quanto tempo meu imóvel entra no ar?",
    answer:
      "Após a vistoria e a sessão de fotos, o anúncio costuma ser publicado em poucos dias.",
  },
  {
    question: "Posso anunciar para venda e locação ao mesmo tempo?",
    answer:
      "Sim, é possível, e isso é definido junto com você conforme o objetivo e o momento do mercado.",
  },
  {
    question: "Como as visitas são organizadas?",
    answer:
      "Todas as visitas são agendadas e acompanhadas pela equipe, garantindo segurança e filtragem de interessados reais.",
  },
  {
    question: "Vocês cuidam da documentação até a escritura?",
    answer:
      "Sim. A Top Class Imóveis acompanha o proprietário em toda a etapa de documentação e fechamento do negócio.",
  },
  {
    question: "Meu imóvel é fora da Zona Sul/Barra da Tijuca, posso anunciar mesmo assim?",
    answer:
      "O foco de atuação é Zona Sul e Barra da Tijuca/Recreio, mas outras regiões podem ser avaliadas sob consulta pelo WhatsApp.",
  },
];

const selectClass =
  "w-full h-10 rounded-md border border-input bg-background px-3 text-sm font-body text-foreground";

const AnuncieSeuImovel = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);

  usePageMeta({
    title: "Anuncie seu Imóvel no Rio | TopClass Imóveis Premium",
    description:
      "Anuncie seu imóvel de alto padrão no Rio: avaliação gratuita, fotos profissionais, portais, campanhas e suporte até a venda. CRECI-RJ 83590.",
    keywords:
      "Anunciar imóvel Rio de Janeiro, Vender apartamento Zona Sul, Anunciar imóvel alto padrão, Imobiliária para vender imóvel Barra da Tijuca, Colocar imóvel para venda RJ",
    ogTitle: "Anuncie seu imóvel com quem entende de alto padrão",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!consent) {
      toast({ title: "Autorize o contato para continuar.", variant: "destructive" });
      return;
    }
    const fd = new FormData(e.currentTarget);
    const get = (k: string) => String(fd.get(k) || "").trim();

    setLoading(true);
    try {
      const { error } = await submitCrmLead({
        name: get("name"),
        phone: get("phone"),
        email: get("email"),
        propertyName: "Anuncie seu imóvel (proprietário)",
        temperature: "quente",
        notes: [
          `Melhor horário: ${get("bestTime")}`,
          `Endereço: ${get("address") || "não informado"}`,
          `Tipo: ${get("type")}`,
          `Finalidade: ${get("purpose")}`,
          `Metragem: ${get("area") || "-"} m²`,
          `Quartos: ${get("bedrooms") || "-"} | Suítes: ${get("suites") || "-"} | Vagas: ${get("garage") || "-"}`,
          `Valor pretendido: ${get("price") || "não informado"}`,
          `Situação: ${get("occupancy")}`,
          get("features") && `Diferenciais: ${get("features")}`,
        ].filter(Boolean) as string[],
      });
      if (error) throw error;
      sessionStorage.setItem("tc_return_to", "/anuncie-seu-imovel");
      navigate(THANK_YOU.anuncie);
    } catch {
      toast({
        title: "Não foi possível enviar",
        description: "Tente novamente ou fale com Paulo Miranda no WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="relative">
          <img
            src={heroImage}
            alt="Fachada de edifício residencial de alto padrão em Ipanema, Rio de Janeiro"
            width={1920}
            height={1088}
            className="w-full h-[52vh] md:h-[70vh] object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-primary/70" />
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-4 max-w-3xl text-center md:text-left">
              <span className="inline-block font-body text-xs tracking-[0.25em] text-accent mb-4">
                CRECI-RJ 83590
              </span>
              <h1 className="font-display text-3xl md:text-5xl font-bold text-white leading-tight">
                Anuncie seu imóvel com quem entende de alto padrão
              </h1>
              <p className="font-body text-white/85 mt-4 text-base md:text-lg">
                Marketing profissional, fotos de qualidade e a rede de contatos certa para vender ou
                alugar seu imóvel na Zona Sul e Barra da Tijuca.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-7 justify-center md:justify-start">
                <Button variant="gold" size="lg" asChild>
                  <a href="#formulario" title="Ir para o formulário de anúncio do imóvel">Quero anunciar meu imóvel</a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/40 hover:bg-white/20"
                  asChild
                >
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" title="Falar com Paulo Miranda no WhatsApp">
                    Falar com Paulo Miranda
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Por que anunciar */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground text-center mb-10">
              Por que anunciar com a Top Class Imóveis
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {reasons.map((r) => (
                <div key={r.title} className="border border-border rounded-lg p-6 bg-card">
                  <r.icon className="h-7 w-7 text-accent mb-4" />
                  <h3 className="font-display text-lg font-bold text-foreground mb-2">{r.title}</h3>
                  <p className="font-body text-sm text-muted-foreground">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Como funciona */}
        <section className="py-16 bg-primary">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-foreground text-center mb-10">
              Como funciona
            </h2>
            <ol className="space-y-6">
              {steps.map((s, i) => (
                <li key={s.title} className="flex gap-4">
                  <span className="flex-shrink-0 h-10 w-10 rounded-full bg-accent text-primary font-display font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-primary-foreground">
                      {s.title}
                    </h3>
                    <p className="font-body text-sm text-primary-foreground/75 mt-1">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* O que está incluso */}
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              O que está incluso
            </h2>
            <ul className="grid gap-4 sm:grid-cols-2">
              {included.map((item) => (
                <li key={item} className="flex gap-3 font-body text-muted-foreground">
                  <Check className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Formulário */}
        <section id="formulario" className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-2xl">
            <div className="rounded-xl p-6 md:p-9 bg-gradient-to-b from-[#3a3a3a] to-[#2b2b2b]">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-6">
                Cadastre seu imóvel
              </h2>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="space-y-4">
                  <h3 className="font-display text-lg text-accent">Dados do proprietário</h3>
                  <div>
                    <Label className="text-white/90" htmlFor="name">Nome completo *</Label>
                    <Input id="name" name="name" required className="mt-1" />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="text-white/90" htmlFor="phone">WhatsApp *</Label>
                      <Input id="phone" name="phone" required className="mt-1" placeholder="(21) 99999-9999" />
                    </div>
                    <div>
                      <Label className="text-white/90" htmlFor="email">E-mail *</Label>
                      <Input id="email" name="email" type="email" required className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/90" htmlFor="bestTime">Melhor horário para contato</Label>
                    <select id="bestTime" name="bestTime" className={`${selectClass} mt-1`} defaultValue="Manhã">
                      <option>Manhã</option>
                      <option>Tarde</option>
                      <option>Noite</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-display text-lg text-accent">Dados do imóvel</h3>
                  <div>
                    <Label className="text-white/90" htmlFor="address">
                      Endereço completo (rua, número, bairro) *
                    </Label>
                    <Input id="address" name="address" required className="mt-1" />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="text-white/90" htmlFor="type">Tipo de imóvel</Label>
                      <select id="type" name="type" className={`${selectClass} mt-1`} defaultValue="Apartamento">
                        <option>Apartamento</option>
                        <option>Casa</option>
                        <option>Cobertura</option>
                        <option>Sala comercial</option>
                        <option>Terreno</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-white/90" htmlFor="purpose">Finalidade</Label>
                      <select id="purpose" name="purpose" className={`${selectClass} mt-1`} defaultValue="Venda">
                        <option>Venda</option>
                        <option>Locação</option>
                        <option>Venda e locação</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-4">
                    <div>
                      <Label className="text-white/90" htmlFor="area">Metragem (m²)</Label>
                      <Input id="area" name="area" type="number" min="0" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-white/90" htmlFor="bedrooms">Quartos</Label>
                      <Input id="bedrooms" name="bedrooms" type="number" min="0" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-white/90" htmlFor="suites">Suítes</Label>
                      <Input id="suites" name="suites" type="number" min="0" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-white/90" htmlFor="garage">Vagas</Label>
                      <Input id="garage" name="garage" type="number" min="0" className="mt-1" />
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="text-white/90" htmlFor="price">Valor pretendido (opcional)</Label>
                      <Input id="price" name="price" className="mt-1" placeholder="Se já tiver uma referência" />
                    </div>
                    <div>
                      <Label className="text-white/90" htmlFor="occupancy">O imóvel está</Label>
                      <select id="occupancy" name="occupancy" className={`${selectClass} mt-1`} defaultValue="Desocupado">
                        <option>Desocupado</option>
                        <option>Ocupado pelo proprietário</option>
                        <option>Alugado</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/90" htmlFor="features">Diferenciais do imóvel</Label>
                    <Textarea
                      id="features"
                      name="features"
                      rows={3}
                      className="mt-1"
                      placeholder="Vista, reforma, mobiliado, lazer do condomínio..."
                    />
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(Boolean(v))}
                    className="mt-1 border-white/40"
                  />
                  <Label htmlFor="consent" className="font-body text-xs text-white/80 leading-relaxed">
                    Autorizo o contato da Top Class Imóveis Premium com as informações acima.
                  </Label>
                </div>
                <Button variant="gold" size="lg" className="w-full" type="submit" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Quero anunciar meu imóvel
                </Button>
                <p className="font-body text-xs text-white/60 text-center italic">
                  Um especialista entra em contato em até 24h úteis para agendar a vistoria.
                </p>
              </form>
            </div>
          </div>
        </section>

        <FaqSection
          title="Perguntas Frequentes — Anuncie seu Imóvel"
          items={faq}
          scriptId="anuncie"
        />

        {/* CTA final */}
        <section className="py-16 bg-primary text-center">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
              Pronto para anunciar seu imóvel com quem entende do mercado de alto padrão?
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="gold" size="lg" asChild>
                <a href="#formulario" title="Ir para o formulário de anúncio do imóvel">Cadastrar meu imóvel agora</a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-primary-foreground border-primary-foreground/40 hover:bg-primary-foreground/10"
                asChild
              >
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" title="Falar com Paulo Miranda no WhatsApp">
                  Falar no WhatsApp (+55 21 97354-8387)
                </a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
        <WhatsAppFloatingButton />
      </main>
    </div>
  );
};

export default AnuncieSeuImovel;
